import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://znexxus.mx/api';

function MeliQuestionsWidget({ user }) {
  const [stats, setStats] = useState({ total: 0, today: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draftText, setDraftText] = useState('');
  const [itemDetails, setItemDetails] = useState({});


  // Validar rol
  const role = (user?.rol || '').toString().trim().toLowerCase();
  const isAllowed = role === 'admin' || role === 'asesor';

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/meli/questions/pending`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching Meli stats:', err);
    }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/meli/questions/unanswered`);
      if (res.ok) {
        const data = await res.json();
        setQuestions(data);
        
        // Fetch item details for unique item_ids
        const uniqueItemIds = [...new Set(data.map(q => q.item_id))].filter(Boolean);
        uniqueItemIds.forEach(fetchItemDetails);
      }
    } catch (err) {
      console.error('Error fetching Meli questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchItemDetails = async (itemId) => {
    if (!itemId) return;
    try {
      // Usamos el proxy del backend para evitar problemas de CORS o requests bloqueados
      const res = await fetch(`${API_BASE_URL}/meli/items/${itemId}`);
      if (res.ok) {
        const data = await res.json();
        setItemDetails(prev => ({ ...prev, [itemId]: { title: data.title, thumbnail: data.thumbnail } }));
      }
    } catch (err) {
      console.error('Error fetching item details:', err);
    }
  };

  const syncQuestions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/meli/questions/sync`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        alert(`Sincronización exitosa. Se procesaron ${data.processed} nuevas preguntas.`);
        fetchStats();
        fetchQuestions();
      } else {
        alert('Error al sincronizar');
      }
    } catch (err) {
      console.error('Error syncing Meli questions:', err);
      alert('Error de conexión al sincronizar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAllowed) return;

    fetchStats();

    // Conectar a Socket.IO
    const socket = io(API_BASE_URL.replace('/api', ''), { path: '/api/socket.io' });
    
    socket.on('new_meli_question', (q) => {
      // Actualizar contador y lista si está abierto
      fetchStats();
      setQuestions(prev => [q, ...prev]);
    });

    return () => socket.disconnect();
  }, [isAllowed]);

  const openModal = () => {
    setIsOpen(true);
    fetchQuestions();
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const startEdit = (q) => {
    setEditingId(q.meli_question_id);
    setDraftText(q.ai_response || '');
  };

  const sendAnswer = async (questionId) => {
    if (!draftText.trim()) return alert('El texto no puede estar vacío');
    try {
      const res = await fetch(`${API_BASE_URL}/meli/questions/${questionId}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: draftText })
      });
      if (res.ok) {
        // Remover de la lista y actualizar stats
        setQuestions(prev => prev.filter(q => q.meli_question_id !== questionId));
        setStats(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
        setEditingId(null);
      } else {
        alert('Error al enviar la respuesta');
      }
    } catch (err) {
      console.error(err);
      alert('Error de conexión');
    }
  };

  if (!isAllowed) return null;

  return (
    <>
      {/* Burbuja Icono */}
      <button 
        onClick={openModal}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none flex items-center justify-center mr-4"
        title="Preguntas de Mercado Libre"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
        </svg>
        {stats.total > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full transform translate-x-1/4 -translate-y-1/4">
            {stats.total}
          </span>
        )}
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-fade-in-up">
            {/* Header */}
            <div className="bg-blue-600 text-white p-4 font-semibold flex justify-between items-center">
              <span className="text-lg flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path></svg>
                Mercado Libre - Preguntas
              </span>
              <div className="flex items-center space-x-4">
                <button 
                  onClick={syncQuestions}
                  disabled={loading}
                  className="text-sm bg-blue-500 hover:bg-blue-400 px-3 py-1.5 rounded-lg transition-colors flex items-center shadow-sm"
                  title="Sincronizar manualmente"
                >
                  <svg className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                  Sync
                </button>
                <span className="text-sm bg-blue-700 px-3 py-1 rounded-lg shadow-inner">{stats.today} hoy</span>
                <button onClick={closeModal} className="text-white hover:text-gray-200 transition-colors p-1">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
            </div>
            
            {/* Body */}
            <div className="overflow-y-auto p-6 space-y-6 bg-gray-50 flex-1">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <svg className="w-10 h-10 text-blue-500 animate-spin mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                  <p className="text-gray-500 font-medium">Cargando preguntas...</p>
                </div>
              ) : questions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path></svg>
                  <p className="font-medium text-lg">No hay preguntas pendientes</p>
                  <p className="text-sm">¡Todo está al día!</p>
                </div>
              ) : (
                questions.map(q => {
                  const item = itemDetails[q.item_id];
                  return (
                  <div key={q.meli_question_id} className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm flex flex-col transition-all hover:shadow-md">
                    {/* Publicación Info */}
                    {item ? (
                      <div className="flex items-center space-x-4 mb-4 pb-4 border-b border-gray-100">
                        <img src={item.thumbnail} alt={item.title} className="w-14 h-14 object-cover rounded-lg border border-gray-200" />
                        <a href={`https://articulo.mercadolibre.com.mx/${q.item_id}`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline line-clamp-2">
                          {item.title}
                        </a>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-4 mb-4 pb-4 border-b border-gray-100 animate-pulse">
                        <div className="w-14 h-14 bg-gray-200 rounded-lg"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    )}

                    <div className="flex items-start mb-4">
                      <div className="bg-gray-100 p-3 rounded-lg rounded-tl-none mt-2 inline-block max-w-full">
                        <p className="text-gray-800 font-medium text-base">"{q.text}"</p>
                      </div>
                    </div>
                    
                    {editingId === q.meli_question_id ? (
                      <div className="mt-2 bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <textarea 
                          className="w-full border border-gray-300 rounded-lg p-3 text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" 
                          rows="4"
                          value={draftText}
                          onChange={(e) => setDraftText(e.target.value)}
                        />
                        <div className="flex gap-3 mt-4 justify-end">
                          <button onClick={() => setEditingId(null)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">Cancelar</button>
                          <button onClick={() => sendAnswer(q.meli_question_id)} className="px-5 py-2 text-sm font-bold bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm transition-colors flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
                            Enviar a Mercado Libre
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 p-5 bg-yellow-50 border border-yellow-200 rounded-xl relative">
                        <span className="text-xs font-bold text-yellow-800 uppercase tracking-wider mb-2 block flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                          Borrador IA:
                        </span>
                        <p className="text-sm text-gray-700 italic leading-relaxed whitespace-pre-wrap">
                          {q.ai_response || 'Generando... (Si está vacío, escribe tu propia respuesta)'}
                        </p>
                        <div className="mt-5 text-right">
                          <button 
                            onClick={() => startEdit(q)}
                            className="px-5 py-2 text-sm bg-yellow-400 text-yellow-900 rounded-lg font-bold hover:bg-yellow-500 shadow-sm transition-colors flex items-center inline-flex"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                            Editar y Enviar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )})
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MeliQuestionsWidget;
